import { useAuth0 } from '@auth0/auth0-react';
import config from '../config';
import { toast, Bounce } from 'react-toastify';

const useCreadyApi = () => {
  const { getAccessTokenSilently, logout } = useAuth0();
  const environment = process.env.REACT_APP_NODE_ENV || 'development';
  const apiUrl = config[environment].apiBaseUrl;
  const companyApiUrl = config[environment].companyApiUrl;

  const createHeaders = async () => {
    const token = await getAccessTokenSilently();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  };

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const get = async (endpoint, retryCount = 0) => {
    try {
      const headers = await createHeaders();
      const response = await fetch(`${apiUrl}/${endpoint}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (retryCount < 2) {
        await sleep(1000);
        return get(endpoint, retryCount + 1);
      }
      await sleep(500);
      logout();
    }
  };

  const getWithId = async (endpoint, id, retryCount = 0) => {
    try {
      const headers = await createHeaders();
      const response = await fetch(`${apiUrl}/clients/${JSON.parse(window.localStorage.getItem('currentClient')).id}/${endpoint}/${id}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (retryCount < 2) {
        await sleep(1000);
        return getWithId(endpoint, id, retryCount + 1);
      }
      await sleep(500);
      showToastBasedOnStatusCode(500);
    }
  };

  const deleteWithId = async (endpoint, id, retryCount = 0) => {
    try {
      const headers = await createHeaders();
      const response = await fetch(`${apiUrl}/clients/${JSON.parse(window.localStorage.getItem('currentClient')).id}/${endpoint}/${id}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (retryCount < 2) {
        await sleep(1000);
        return deleteWithId(endpoint, id, retryCount + 1);
      }
      await sleep(500);
      showToastBasedOnStatusCode(500);
    }
  };

  const getFileById = async (endpoint, id, retryCount = 0) => {
    try {
      const headers = await createHeaders();
      const response = await fetch(`${apiUrl}/clients/${JSON.parse(window.localStorage.getItem('currentClient')).id}/${endpoint}/${id}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.blob();
      return data;
    } catch (error) {
      if (retryCount < 2) {
        await sleep(1000);
        return getFileById(endpoint, id, retryCount + 1);
      }
      await sleep(500);
      showToastBasedOnStatusCode(500);
    }
  };

  const getWithClient = async (endpoint, retryCount = 0) => {
    try {
      const headers = await createHeaders();
      const response = await fetch(`${apiUrl}/clients/${JSON.parse(window.localStorage.getItem('currentClient')).id}/${endpoint}`, {
        method: 'GET',
        headers,
      });
      if(response.status === 404){
        return null;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (retryCount < 2) {
        await sleep(1000);
        return getWithClient(endpoint, retryCount + 1);
      }
      await sleep(500);
      showToastBasedOnStatusCode(500);
    }
  };

  const getWithParams = async (endpoint, paramsArray, retryCount = 0) => {
    try {
      const headers = await createHeaders();

      const queryString = paramsArray
        .map(param => Object.entries(param).map(([key, value]) => `${key}=${value}`).join('&'))
        .join('&');

      const response = await fetch(`${apiUrl}/clients/${JSON.parse(window.localStorage.getItem('currentClient')).id}/${endpoint}?${queryString}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (retryCount < 2) {
        await sleep(1000);
        return getWithParams(endpoint, paramsArray, retryCount + 1);
      }
      await sleep(500);
      showToastBasedOnStatusCode(500);
    }
  };

  const post = async (endpoint, data, retryCount = 0) => {
    try {
      const headers = await createHeaders();
      const response = await fetch(`${apiUrl}/clients/${JSON.parse(window.localStorage.getItem('currentClient')).id}/${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        showToastBasedOnStatusCode(response.status);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      if (retryCount < 2) {
        await sleep(1000);
        return post(endpoint, data, retryCount + 1);
      }
      await sleep(500);
      showToastBasedOnStatusCode(500);
    }
  };

  const postDirect = async (endpoint, data) => {
    const headers = await createHeaders();
    const response = await fetch(`${apiUrl}/${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) return null;
    return response.json();
  };

  const remove = async (endpoint, data, retryCount = 0) => {
    try {
      const headers = await createHeaders();
      const response = await fetch(`${apiUrl}/clients/${JSON.parse(window.localStorage.getItem('currentClient')).id}/${endpoint}`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        showToastBasedOnStatusCode(response.status);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      if (retryCount < 2) {
        await sleep(1000);
        return remove(endpoint, data, retryCount + 1);
      }
      await sleep(500);
      showToastBasedOnStatusCode(500);
    }
  };

  const api = {
    clients: {
      list: async () => get('clients'),
      summary: async () => getWithClient('summary'),
    },
    debts: {
      getById: async (id) => getWithId('debt', id),
      list: async (params) => getWithParams('debt', params),
      chats: async (id) => getWithClient('debt/' + id + '/chat'),
      summary: async (id) => getWithClient('debt/' + id + '/summary'),
      addChatMessage: async (id, body) => post('debt/' + id + '/chat', body),
      create: async (body) => post('debt', body),
      addPayment: async (body) => post('debt/payment', body),
      withdraw: async (body) => post('debt/withdraw', body),
    },
    invoices:{
      getById: async (id) => getWithId('invoice', id),
      list: async (params) => getWithParams('invoices', params),
      summary: async (id) => getWithClient('invoices/' + id + '/summary')
    },
    file:{
      getFileById: async(id) => getFileById('document',id),
    },
    notification: {
      list: async (id) => getWithClient('notification/')
    },
    integrations: {
      list: async => getWithClient('integrations/'),
      create: async (body) => post('integrations/', body),
      remove: async => remove('integrations'),
    },
    fortnox: {
      finalize: async(params) => getWithParams('fortnox/finalize', params),
      fetch: async(body) => post('fortnox/fetch', body),
      list: async(params) => getWithParams('fortnox/list', params),
      synchronize: async => post('fortnox/synchronize'),
      process: async(body) => post('fortnox/process', body)
    },
    company: {
      lookup: async (orgNr) => {
        const token = await getAccessTokenSilently();
        const response = await fetch(`${companyApiUrl}/company`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ orgNr }),
        });
        if (!response.ok) return null;
        return response.json();
      }
    }
  };

  return api;
};

function showToastBasedOnStatusCode(statusCode) {
  let message;
  let isError = false;

  switch (statusCode) {
    case 201:
      message = 'Resursen skapades!';
      break;
    case 400:
      message = 'Felaktig begäran. Kontrollera dina inmatningar.';
      isError = true;
      break;
    case 401:
      message = 'Ej auktoriserad. Logga in igen.';
      isError = true;
      break;
    case 403:
      message = 'Åtkomst nekad. Du har inte behörighet.';
      isError = true;
      break;
    case 404:
      break;
    case 500:
      message = 'Serverfel. Försök igen senare.';
      isError = true;
      break;
    case 503:
      message = 'Tjänsten är otillgänglig. Försök igen senare.';
      isError = true;
      break;
    case 200:
      break;
    default:
      break;
  }

  if (message) {
    if (isError) {
      toast.error(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
        transition: Bounce,
      });
    } else {
      toast.success(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
        transition: Bounce,
      });
    }
  }
}

export default useCreadyApi;
